import { useCallback, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { DateTime } from 'luxon';

import { UserImportsDocument } from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';

interface IUseUserImportGuardOptions {
    canImport?: boolean;
}

export const useUserImportGuard = ({
    canImport = false,
}: IUseUserImportGuardOptions = {}) => {
    const { showApolloError } = useApolloError();
    const [importBlockedDialogOpen, setImportBlockedDialogOpen] =
        useState(false);

    const [checkRunningImports, { loading: checkingImports }] = useLazyQuery(
        UserImportsDocument,
        { fetchPolicy: 'network-only' }
    );

    const guardedImportClick = useCallback(
        async (fileInputId: string) => {
            if (!canImport) return;

            const oneHourAgo = DateTime.now().minus({ hours: 1 }).toISO();

            const result = await checkRunningImports({
                variables: {
                    complete: false,
                    lastModifiedAfter: oneHourAgo,
                },
            });

            if (result.error) {
                showApolloError(result.error);

                return;
            }

            if (!result.data) return;

            if (result.data.userImports.length) {
                setImportBlockedDialogOpen(true);
            } else {
                document.getElementById(fileInputId)?.click();
            }
        },
        [canImport, checkRunningImports, showApolloError]
    );

    const handleImportBlockedClose = useCallback(
        () => setImportBlockedDialogOpen(false),
        []
    );

    return {
        guardedImportClick,
        checkingImports,
        importBlockedDialogOpen,
        handleImportBlockedClose,
    };
};
